import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, Check, X, Clock, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        try {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`);
          
          if (response.data.payment_status === "paid") {
            setStatus("success");
            setPaymentData(response.data);
            return;
          } else if (response.data.status === "expired") {
            setStatus("expired");
            return;
          }

          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            setStatus("pending");
            setPaymentData(response.data);
          }
        } catch (error) {
          setStatus("error");
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [sessionId]);

  const handleHealthForm = () => {
    const params = new URLSearchParams();
    if (paymentData?.member_id) params.set("member_id", paymentData.member_id);
    if (paymentData?.transaction_id) params.set("transaction_id", paymentData.transaction_id);
    navigate(`/health-form?${params.toString()}`);
  };

  return (
    <>
      <SEO 
        title="Payment Confirmation"
        description="Payment confirmation page for Glamour Body services."
        keywords="payment confirmation, booking confirmed, Glamour Body"
      />
      
      <div className="min-h-screen flex items-center justify-center px-4 pt-24 aurora-bg" data-testid="payment-success-page">
        <div className="orb-cyan top-20 left-20" />
        <div className="orb-purple bottom-20 right-20" />
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card rounded-2xl p-8 max-w-md w-full text-center relative z-10"
        >
          {status === "checking" && (
            <>
              <Loader2 className="mx-auto mb-4 h-12 w-12 text-cyan-400 animate-spin" />
              <h1 className="font-italiana text-2xl text-white mb-2">Processing Payment</h1>
              <p className="text-white/60">Please wait while we confirm your payment...</p>
            </>
          )}
          
          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-white" />
              </div>
              <h1 className="font-italiana text-2xl text-white mb-2">
                {paymentData?.is_gift_card ? "Gift Card Sent!" : "Payment Successful!"}
              </h1>
              <p className="text-white/60 mb-6">
                {paymentData?.is_gift_card 
                  ? "The gift card has been emailed to the recipient. Thank you for your purchase!"
                  : "Thank you for your booking. Please complete the health history form below."
                }
              </p>
              {paymentData?.metadata?.service_name && !paymentData?.is_gift_card && (
                <div className="glass-card rounded-xl p-4 mb-6">
                  <p className="text-white/70 text-sm">Service Booked</p>
                  <p className="text-white font-semibold">{paymentData.metadata.service_name}</p>
                </div>
              )}
              
              {/* Health Form Button - only for non-gift-card purchases */}
              {!paymentData?.is_gift_card && (
                <Button 
                  onClick={handleHealthForm} 
                  className="w-full btn-primary rounded-full px-8 mb-3 py-4"
                  data-testid="health-form-btn"
                >
                  <FileText className="mr-2" size={18} />
                  Complete Health Form
                </Button>
              )}
              
              <Button 
                onClick={() => navigate(paymentData?.is_gift_card ? "/gift-cards" : "/")} 
                variant="ghost"
                className="text-white/60 hover:text-white"
              >
                {paymentData?.is_gift_card ? "Send Another Gift Card" : "Skip for now"}
              </Button>
            </>
          )}
          
          {status === "pending" && (
            <>
              <Clock className="mx-auto mb-4 h-12 w-12 text-yellow-400" />
              <h1 className="font-italiana text-2xl text-white mb-2">Payment Processing</h1>
              <p className="text-white/60 mb-6">
                Your payment is being processed. You'll receive an email confirmation shortly.
              </p>
              <Button onClick={() => navigate("/")} className="btn-primary rounded-full px-8">
                Return Home
              </Button>
            </>
          )}
          
          {(status === "error" || status === "expired") && (
            <>
              <X className="mx-auto mb-4 h-12 w-12 text-red-400" />
              <h1 className="font-italiana text-2xl text-white mb-2">
                {status === "expired" ? "Session Expired" : "Something Went Wrong"}
              </h1>
              <p className="text-white/60 mb-6">
                {status === "expired" 
                  ? "Your payment session has expired. Please try booking again."
                  : "We couldn't process your payment. Please try again or contact us."
                }
              </p>
              <Button onClick={() => navigate("/services")} className="btn-primary rounded-full px-8">
                Try Again
              </Button>
            </>
          )}
        </motion.div>
      </div>
    </>
  );
};

export default PaymentSuccessPage;
